<template>
  <v-dialog
    :value="enableDialog"
    persistent
    width="900"
    @keydown.esc="closeButtonClickEvent"
    @close="closeButtonClickEvent"
  >
    <validation-observer ref="transactionDetailValidator">
      <v-card>
        <v-card-title>
          売上明細の登録
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh;overflow-y: auto ">
          <!-- 売上品目／課税区分 -->
          <v-row>
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="売上品目"
                rules="required"
              >
                <v-autocomplete
                  v-model="forms.sales_item_price_code"
                  :error-messages="errors"
                  :items="salesItemPriceDetails"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details="auto"
                  label="売上品目"
                  outlined
                  persistent-placeholder
                  @change="salesItemPriceDetailChangeEvent"
                >
                </v-autocomplete>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /取扱品目／課税区分 -->

          <!-- マニフェスト数量／回収数量 -->
          <v-row
            v-if="forms.manifest_available"
          >
            <v-col cols="12" md="4">
              <v-text-field
                v-model="forms.manifest_quantity"
                :show-size="1000"
                disabled
                dense
                filled
                label="マニフェスト数量"
                hide-details="auto"
                outlined
                persistent-placeholder
                reverse
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="forms.item_name"
                disabled
                dense
                filled
                label="JWNET取扱品目"
                hide-details="auto"
                outlined
                persistent-placeholder
                required
              />
            </v-col>
          </v-row>
          <!-- /マニフェスト数量／回収数量 -->

          <!-- 運搬量／確定数量 -->
          <v-row
            v-if="forms.manifest_available"
          >
            <v-col cols="12" md="4">
              <v-text-field
                v-model="forms.manifest_transport_amount"
                disabled
                dense
                filled
                label="運搬量"
                hide-details="auto"
                outlined
                persistent-placeholder
                reverse
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="forms.fixed_quantity"
                :show-size="1000"
                disabled
                dense
                filled
                label="確定数量"
                hide-details="auto"
                outlined
                persistent-placeholder
                reverse
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="forms.packing_name"
                disabled
                dense
                filled
                label="荷姿"
                hide-details="auto"
                outlined
                persistent-placeholder
              />
            </v-col>
          </v-row>
          <!-- /運搬量／確定数量 -->

          <!-- 取引数量／単価／単価単位 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                :custom-messages="customMessages"
                :rules="{required: true, double: true, min_value: 0.001, max_value: 99999.999, quantityWithDecimal: { int_digits: 5, dec_digits: 3 } }"
                name="取引数量"
              >
                <v-text-field
                  v-model="forms.quantity"
                  :error-messages="errors"
                  :show-size="1000"
                  dense
                  label="取引数量"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  required
                  type="number"
                  @keydown.69.prevent
                  @change="quantityOrPriceChangeEvent"
                />
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                rules="required"
                name="取引単位"
              >
                <v-select
                  v-model="forms.sales_item_unit_id"
                  :items="salesItemUnits"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="取引単位"
                  outlined
                  persistent-placeholder
                  style="font-size: 90%;"
                  @change="setUnitPrice"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                :custom-messages="customMessages"
                :rules="{required: true, double: true, min_value: -9999999.999, max_value: forms.sales_item_type === 'purchase_item' ? 0 : 9999999.999 , quantityWithDecimal: { int_digits: 7, dec_digits: 3 } }"
                name="単価"
              >
                <v-text-field
                  v-model="forms.unit_price"
                  :error-messages="errors"
                  :show-size="1000"
                  dense
                  label="単価"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  required
                  type="number"
                  @keydown.69.prevent
                  @change="quantityOrPriceChangeEvent"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                :custom-messages="customMessages"
                :rules="{required: true, double: true, min_value: 0.001, max_value: 99999.999, quantityWithDecimal: { int_digits: 5, dec_digits: 3 } }"
                name="単位あたり"
              >
                <v-text-field
                  v-model="forms.quantity_per_unit"
                  :error-messages="errors"
                  :show-size="1000"
                  dense
                  label="単位あたり"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  required
                  type="number"
                  @keydown.69.prevent
                  @change="quantityOrPriceChangeEvent"
                >
                </v-text-field>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="2">
              <span>
                {{ forms.sales_item_unit_name }}
              </span>
            </v-col>

          </v-row>
          <!-- /数量／単価／単価単位 -->

          <!-- 明細金額 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :custom-messages="customMessages"
                name="明細金額"
              >
                <v-text-field
                  v-model="forms.detailed_amount"
                  :error-messages="errors"
                  :show-size="1000"
                  disabled
                  dense
                  filled
                  label="明細金額"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  type="number"
                  @keydown.69.prevent
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                :value="taxTypeName"
                disabled
                dense
                filled
                label="課税区分"
                hide-details="auto"
                outlined
                persistent-placeholder
              >
              </v-text-field>
            </v-col>
          </v-row>
          <!-- 明細金額 -->

          <!-- 売上明細備考 -->
          <v-row>
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                name="売上明細備考"
                rules="max:25"
              >
                <v-text-field
                  v-model="forms.detailed_memo"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="売上明細備考"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>売上明細備考</span>
                    <span class="ml-1">
                      ({{ forms.detailed_memo?.length || 0 }} / 25)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- 売上明細備考 -->
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="closeButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            class="primary"
            @click="reflectButtonClickEvent"
          >
            反映
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'

export default {
  name: 'TransactionDetailDialog',
  components: {},
  mixins: [],
  props: {
    /** @param enableDialog { Boolean } ダイアログの制御 */
    enableDialog: {
      type: Boolean,
      default: () => false,
    },
    /** 取引先ID */
    supplierId: {
      type: Number,
      default: () => null,
    },
    /** 取引先種別 */
    supplierType: {
      type: String,
      default: () => null
    },
    /** 端数処理区分 */
    fractionType: {
      type: String,
      default: () => null
    },
    transactionDetail: {
      type: Object,
      default: () => null,
    },
    callerMode: {
      type: String,
      default: () => null,
    },
    targetDate: {
      type: String,
      default: () => null
    },
    salesItemUnits: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      customMessages: {
        double: '数量は半角数字のみ使用できます'
      },
      //  描画
      forms: {
        id: null,

        manifest_available: false,

        key: null,

        item_id: null,

        item_name: null,

        // 取引日
        trading_date: null,
        // 売上計上日
        sales_date: null,
        // 売上品目コード
        sales_item_price_code: null,
        // 売上品目名
        sales_item_price_name: null,
        // 品目区分
        sales_item_type: null,
        // 課税区分
        tax_type: null,
        // 取引数量
        quantity: null,
        // 取引単位ID
        sales_item_unit_id: null,
        // 単価
        unit_price: null,
        // 単位あたり数量
        quantity_per_unit: null,
        // 単価単位名称
        sales_item_unit_name: null,
        // 荷姿名称
        packing_name: null,
        // 荷姿ID
        packing_id: null,
        detailed_amount: 0,
        // 端数処理種別
        fraction_type: null,
        // 備考
        detailed_memo: null,

        manifest_id: null,
        manifest_quantity:null,
        manifest_transport_amount: null,

        _destroy: false,
      },
      selectedItemPriceUnitId: null,
      /** 選択された売上品目単価と紐付く売上品目単位の名称 */
      selectedItemPriceUnitName: null,
      /** 売上品目単価 */
      salesItemPriceDetails: [],
    }
  },
  computed: {
    taxTypeName () {
      let value = null
      switch (this.forms.tax_type) {
        case 'on_tax':
          value = '課税'
          break
        case 'non_tax':
          value = '非課税'
          break
        default:
          value = null
      }
      return value
    },
  },
  watch: {
    /**
     * ダイアログ表示制御フラグの監視
     * @param newValue { Boolean }
     */
    enableDialog (newValue) {
      this.selectedItemPriceUnitName = null
      if (newValue) {
        this.setVariable()
      }
    },
  },
  created() {},
  methods: {
    /** 売上品目 */
    ...mapActions('DriverMasterSearchSupplierSalesItemPriceDetail', {
      fetchSalesItemPriceDetails: 'index_with_reference_date',
    }),
    /**
     * 反映するボタン クリックイベント
     */
    reflectButtonClickEvent () {
      this.$refs.transactionDetailValidator.validate().then((result) => {
        if (result) {
          let result = _.cloneDeep(this.forms)
          this.$emit('reflectEvent', result)
        }
      })
    },
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('closeEvent')
    },
    /**
     * 売上品目単位のチェンジイベント
     */
    salesItemPriceDetailChangeEvent () {
      const salesItemPrice = this.salesItemPriceDetails.find( v => v.value === this.forms.sales_item_price_code)
      if (salesItemPrice) {
        this.forms.item_id = salesItemPrice.item_id
        this.forms.item_name = salesItemPrice.item_name
        this.forms.sales_item_price_name = salesItemPrice.name
        this.forms.sales_item_type = salesItemPrice.sales_item_type
        this.forms.tax_type = salesItemPrice.tax_type
        this.forms.sales_item_unit_price = salesItemPrice.unit_price
        this.selectedItemPriceUnitId = salesItemPrice.sales_item_unit_id
        this.selectedItemPriceUnitName = salesItemPrice.sales_item_unit_name
        this.forms.quantity_per_unit = 1
        // 単価設定
        this.setUnitPrice()
      }
    },
    /**
     * 取引数量または単価のチェンジイベント
     */
    quantityOrPriceChangeEvent () {
      let quantity = this.forms.quantity || 0
      let unit_price = this.forms.unit_price || 0
      let quantity_per_unit = this.forms.quantity_per_unit || 0

      let sum_price = 0
      // 切り捨て
      if (this.fractionType === "rounded_down") {
        sum_price = Math.floor((quantity * (unit_price / quantity_per_unit) * Math.pow(10, 3)) / Math.pow(10, 3))
        // 切り上げ
      } else if (this.fractionType === "rounded_up") {
        sum_price = Math.ceil((quantity * (unit_price / quantity_per_unit) * Math.pow(10, 3)) / Math.pow(10, 3))
        // 四捨五入
      } else {
        sum_price = Math.round((quantity * (unit_price / quantity_per_unit) * 1000) / 1000)
      }
      this.forms.detailed_amount = sum_price
    },
    /**
     * 単価を設定する 売上品目単位名と、画面の取引単位名が文字列比較で同じ場合、単価を上書きセット
     */
    setUnitPrice() {
      const salesItemUnit = this.salesItemUnits.find((v) => v.value === this.forms.sales_item_unit_id)
      if (salesItemUnit) {
        this.forms.sales_item_unit_name = salesItemUnit.text
      }
      this._setSalesItemUnitNameToForm()
      if (this.forms.sales_item_unit_name === this.selectedItemPriceUnitName) {
        this.forms.unit_price = this.forms.sales_item_unit_price
      } else {
        this.forms.unit_price = 0
      }
      this.quantityOrPriceChangeEvent()
    },
    /**
     * 画面項目の初期化
     */
    setVariable () {
      Object.assign(this.$data.forms, this.$options.data().forms)
      this.$nextTick(() => this.$refs.transactionDetailValidator.reset())
      this.fetchSalesItemPriceDetails({id: this.supplierId, type: this.supplierType, only_jwnet_item: false, reference_date: this.targetDate}).then((content) => {
        this.salesItemPriceDetails = content.sales_item_price_details
        if (this.transactionDetail) {
          this.forms = _.cloneDeep(this.transactionDetail)
          this._setSalesItemUnitNameToForm()
        } else {
          this.forms.key = Math.round( Math.random()*1000000 )
        }
      })
    },
    /**
     * 選択されている売上品目から品目単位をメモリ上にセットする.
     * @private
     */
    _setSalesItemUnitNameToForm () {
      const salesItemPrice = this.salesItemPriceDetails.find((v) => v.value === this.forms.sales_item_price_code)
      if (salesItemPrice) {
        // if (!this.forms.sales_item_unit_name) {
          this.forms.item_id = salesItemPrice.item_id
          this.forms.item_name = salesItemPrice.item_name
          this.forms.sales_item_price_name = salesItemPrice.name
          this.forms.sales_item_type = salesItemPrice.sales_item_type
          this.forms.tax_type = salesItemPrice.tax_type
          this.forms.sales_item_unit_price = salesItemPrice.unit_price
          this.selectedItemPriceUnitId = salesItemPrice.sales_item_unit_id
          this.selectedItemPriceUnitName = salesItemPrice.sales_item_unit_name
        // }
      }
    }

  }
}
</script>

<style scoped lang='scss'>

</style>


